import { Paper, Table, Text, Title } from "@mantine/core";
import { PDFPage } from "./PDFPage";
import useReportStore from "../../../store/useReportStore";
import { SkillsAssessment } from "../../../types/api";
import { useTranslation } from "react-i18next";

interface SkillAssessment {
  title: {
    text: string;
    color: string;
  };
  percentage: number;
  skills: string;
  master: string;
}

const transformSkillsAssessment = (
  skillsAssessments: SkillsAssessment[]
): { skills_assessment: SkillAssessment[] } => {
  const colors = ["#05D8D2", "#3A8BD9", "#6F40E2", "#A64DD7", "#DE57CC"];

  return {
    skills_assessment: skillsAssessments.map((assessment, index) => ({
      title: {
        text: assessment.title,
        color: colors[index % colors.length],
      },
      percentage: assessment.fit_percentage,
      skills: assessment.skills_you_have
        ? assessment.skills_you_have.join(", ")
        : "",
      master: assessment.skills_to_master
        ? assessment.skills_to_master.join(", ")
        : "",
    })),
  };
};

export const SkillsAssessmentPDF = () => {
  const { report } = useReportStore();
  const { t } = useTranslation();

  const content = report?.skills_assessment || [];

  const transformedContent = transformSkillsAssessment(content.data);

  const rows = transformedContent.skills_assessment.map((element, index) => (
    <Table.Tr key={index}>
      <Table.Td
        style={{ width: "30%", wordBreak: "break-word" }}
        px={20}
        py={12}
      >
        <Paper bg={element.title.color} px={20} py={12} radius={12} c="#fff">
          <Text fw="bold" size="20px" lh="30px">
            {element.title.text}
          </Text>
        </Paper>
      </Table.Td>
      <Table.Td style={{ width: "15%", wordBreak: "break-word" }} p={12}>
        <Text size="20px" lh="30px">
          {element.percentage}%
        </Text>
      </Table.Td>
      <Table.Td
        style={{ width: "27.5%", wordBreak: "break-word" }}
        px={20}
        py={12}
      >
        <Text size="20px" lh="30px">
          {element.skills}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: "27.5%", wordBreak: "break-word" }} p={12}>
        <Text size="20px" lh="30px">
          {element.master}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PDFPage padding>
      <Title size={46} mb={16}>
        {content?.mark_down ? content?.mark_down.replace("##", "") : ""}
      </Title>
      <Text mb={48} size="26px" lh="40px" c="dimmed">
        {content?.explanation ? content?.explanation : ""}
      </Text>
      <Paper radius="16px" withBorder>
        <Table highlightOnHover withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "30%" }} px={20} py={12}>
                <Text fw="bold" size="22px" lh="34px">
                  {t("jobTitle")}
                </Text>
              </Table.Th>
              <Table.Th style={{ width: "15%" }} px={20} py={12}>
                <Text fw="bold" size="22px" lh="34px">
                  {t("fitPercentage")}
                </Text>
              </Table.Th>
              <Table.Th style={{ width: "27.5%" }} px={20} py={12}>
                <Text fw="bold" size="22px" lh="34px">
                  {t("skillsYouHave")}
                </Text>
              </Table.Th>
              <Table.Th style={{ width: "27.5%" }} px={20} py={12}>
                <Text fw="bold" size="22px" lh="34px">
                  {t("skillsToMaster")}
                </Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Paper>
    </PDFPage>
  );
};
